import axios from 'axios';
// eslint-disable-next-line camelcase
import jwt_decode from 'jwt-decode';
import { getStore } from './storeRef';
import { setServerDown } from './uiSlice';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  },
  withCredentials: true
});

api.interceptors.request.use(
  (config) => {
    const accessToken = localStorage.getItem('accessToken');

    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

function clearAuthAndRedirect() {
  localStorage.removeItem('accessToken');
  window.location.href = `${process.env.REACT_APP_ADMIN_URL}/login`;
}

// Shared across concurrent 401s so /auth/refresh is only called once even if
// several requests fail at the same time: the refresh token is single-use
// (rotation), so a second concurrent call would fail with the first one's
// leftover token. The refresh token lives in an httpOnly cookie on the API
// domain (sent automatically via withCredentials), so no token is read or
// stored here.
let refreshPromise = null;

const accountOf = (token) => {
  try {
    const { email, phone } = jwt_decode(token);
    return email || phone || null;
  } catch (error) {
    return null;
  }
};

// Both apps share one refresh cookie (API domain): logging in with another
// account on the other app makes the next refresh return that account's
// token. Switching silently would mix the previous account's screen with the
// new account's data, so reload the app on the new account instead.
// Returns true when the page is reloading.
export function storeRefreshedToken(accessToken) {
  const previousAccount = accountOf(localStorage.getItem('accessToken'));
  localStorage.setItem('accessToken', accessToken);

  if (previousAccount && previousAccount !== accountOf(accessToken)) {
    // Belonged to the previous account (set by the guest hand-off).
    localStorage.removeItem('company');
    window.location.reload();
    return true;
  }
  return false;
}

// A "connection failure" is either no response at all (network down, DNS,
// refused) or a gateway-level error status (backend unreachable behind the
// proxy). Those aren't normal app errors (validation, 401, 404...), so we
// surface them as a full-page "server unavailable" screen instead of raw
// axios text leaking into form/UI error states.
//
// A 502/503/504 that carries our API's own JSON error body (NestJS always sends
// `statusCode`) is a normal app error — e.g. "AI not available" (503) or "AI
// generation failed" (502) — and must reach the screen that made the call, not
// replace the whole app. Only a gateway page (proxy up, backend down) or no
// response at all is a real outage.
const isApiErrorBody = (data) =>
  !!data && typeof data === 'object' && typeof data.statusCode === 'number';

const isConnectionFailure = (error) => {
  if (!error.response) return true;
  return (
    [502, 503, 504].includes(error.response.status) &&
    !isApiErrorBody(error.response.data)
  );
};

function refreshSession() {
  if (!refreshPromise) {
    // REACT_APP_API_BASE_URL has a trailing slash, so a plain template
    // concat here produces `//auth/refresh` and 404s - strip it first.
    const baseUrl = process.env.REACT_APP_API_BASE_URL.replace(/\/+$/, '');
    refreshPromise = axios
      .post(`${baseUrl}/auth/refresh`, null, { withCredentials: true })
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
}

// Checks the session now instead of at the next request: when the backend
// says it ended (session_ended on the notifications socket) and when an idle
// tab comes back. Back to login if it is gone, reload if the browser is now on
// another account. Resolves true when the app can carry on.
export async function checkSession() {
  if (!localStorage.getItem('accessToken')) return false;
  try {
    const { data } = await refreshSession();
    return !storeRefreshedToken(data.accessToken);
  } catch (error) {
    // No answer (network down): not a verdict on the session.
    if (!error.response) return true;
    clearAuthAndRedirect();
    return false;
  }
}

api.interceptors.response.use(
  (response) => {
    getStore()?.dispatch(setServerDown(false));
    return response;
  },
  async (error) => {
    getStore()?.dispatch(setServerDown(isConnectionFailure(error)));

    // The company's subscription is blocked (no transfer proof, transfer not
    // approved in time, subscription ended): the backend refuses everything
    // except the payment routes, which live in the guest app.
    if (
      error.response?.status === 403 &&
      error.response.data?.code === 'SUBSCRIPTION_INACTIVE'
    ) {
      window.location.href = process.env.REACT_APP_ADMIN_URL;
      return new Promise(() => {});
    }

    const originalRequest = error.config;
    const isRefreshCall = originalRequest?.url?.includes('/auth/refresh');

    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !isRefreshCall
    ) {
      originalRequest._retry = true;

      try {
        const { data } = await refreshSession();

        // Never settles: the page is reloading on the new account.
        if (storeRefreshedToken(data.accessToken)) return new Promise(() => {});

        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers.Authorization = `Bearer ${data.accessToken}`;

        return api(originalRequest);
      } catch (refreshError) {
        clearAuthAndRedirect();
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export default api;
